/**
 * t189u: one task comment on the phone (the task's comments and the feed's comments sheet): avatar and name open the
 * person; long-press opens the same action sheet as a chat message (quick reactions, Copy); reactions show as pills.
 * t258u: on your own comment the sheet (long-press or the small ⋯) has Copy, Edit and Delete and no reactions, and its
 * pills are read-only; Edit is inline and saves only a real change; edited comments say "(edited)".
 */
import { useEffect, useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import type { TaskComment } from '@tradeoutlet/shared';
import { Avatar } from '@/components/ui';
import { RelativeTime } from '@/components/time/RelativeTime';
import { ItemReactions, useItemReactionToggle } from '@/components/reactions/ItemReactions';
import { ReactionBar } from '@/components/reactions/ReactionBar';
import { PersonButton } from '@/features/people/PersonDrawer';
import { QUICK_EMOJI } from '@/features/chat/MessageItem';
import { MessageBody } from '@/features/chat/MessageBody';
import { MessageSheet } from '@/mobile/MessageSheet';
import { useLongPress } from '@/mobile/longPress';
import { useMembers } from '@/lib/hooks';
import { useAuth } from '@/lib/auth';
import { useUi } from '@/lib/store';
import { timeOfDay } from '@/lib/format';
import { decodeMentions } from '@/lib/mentions';
import { useTaskCommentActions } from './taskCommentActions';
import { enterToSend } from '@/lib/enterToSend';

export function TaskCommentRow({ c }: { c: TaskComment }) {
  const { byId } = useMembers();
  const { userId } = useAuth();
  const toast = useUi(s => s.toast);
  const nameOf = (id: string) => byId.get(id)?.display_name ?? 'someone';
  const name = byId.get(c.author_id)?.display_name ?? 'Someone';
  const mine = Boolean(userId) && c.author_id === userId;
  const [sheet, setSheet] = useState(false);
  const r = useItemReactionToggle('task_comment', c.id);
  const act = useTaskCommentActions(nameOf);
  const editing = act.editing?.id === c.id ? act.editing : null;
  const press = useLongPress(() => { if (!editing) setSheet(true); });
  // Editing the last comment: bring its Save / Cancel up above the comment box at the bottom.
  const editForm = useRef<HTMLFormElement>(null);
  const isEditing = Boolean(editing);
  useEffect(() => { if (isEditing) editForm.current?.scrollIntoView?.({ block: 'nearest' }); }, [isEditing]);
  const copy = () => { void navigator.clipboard?.writeText(decodeMentions(c.body, nameOf).text).then(() => toast('Copied', 'success'), () => undefined); };
  return (
    <div className="no-callout relative flex gap-3 px-4 py-2" data-testid="m-task-comment" data-mine={mine || undefined} {...press}>
      <PersonButton userId={c.author_id} name={name} className="shrink-0 rounded-full"><Avatar size={32} name={name} id={c.author_id} url={byId.get(c.author_id)?.avatar_url} /></PersonButton>
      <div className={mine ? 'min-w-0 flex-1 pr-6' : 'min-w-0 flex-1'}>
        <p className="text-sm"><PersonButton userId={c.author_id} name={name} className="font-semibold text-slate-950 dark:text-white">{name}</PersonButton> <span className="text-xs text-slate-500"><RelativeTime iso={c.created_at} /></span></p>
        {editing ? (
          <form ref={editForm} className="mt-1" onSubmit={e => { e.preventDefault(); void act.save(c); }} data-testid="m-task-comment-edit">
            <textarea autoFocus value={editing.text} onChange={e => act.setText(e.target.value)} rows={2} aria-label="Edit comment" enterKeyHint="send"
              onKeyDown={enterToSend({ value: editing.text, onSend: () => void act.save(c), onCancel: act.cancel })}
              className="w-full resize-none rounded-xl border border-slate-300 bg-white px-3 py-2 text-base focus:border-slate-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900" />
            <div className="mt-1 flex justify-end gap-2">
              <button type="button" onClick={act.cancel} className="h-10 rounded-full px-4 text-sm font-medium text-slate-700 dark:text-slate-200">Cancel</button>
              <button type="submit" disabled={act.busy || !editing.text.trim()} data-testid="m-task-comment-save"
                className="h-10 rounded-full bg-ink px-4 text-sm font-semibold text-white disabled:opacity-50 dark:bg-white dark:text-ink">Save</button>
            </div>
          </form>
        ) : (
          <div className="whitespace-pre-wrap break-words text-[15px]"><MessageBody body={c.body} />{c.edited_at && <span className="text-[11px] text-slate-500" data-testid="comment-edited"> (edited)</span>}</div>
        )}
        {mine ? (r.pills.length > 0 && <ReactionBar className="mt-1" kind="entity" id={`task_comment:${c.id}`} pills={r.pills} onToggle={() => undefined} />)
          : <ItemReactions type="task_comment" id={c.id} pillsOnly className="mt-1" />}
      </div>
      {mine && !editing && (
        <button type="button" aria-label="Comment actions" data-testid="m-task-comment-more" onClick={() => setSheet(true)}
          className="absolute right-1 top-1 flex h-11 w-11 items-center justify-center text-slate-400 active:text-slate-700 dark:text-slate-500"><MoreHorizontal className="h-4 w-4" /></button>
      )}
      {sheet && <MessageSheet title={`${mine ? 'Your comment' : name}, ${timeOfDay(c.created_at)}`} emoji={mine ? [] : QUICK_EMOJI.slice(0, 6)} reacted={r.mine} onReact={e => void r.toggle(e)}
        actions={mine ? ['copy', 'edit', 'delete'] : ['copy']} onAction={a => { if (a === 'copy') copy(); else if (a === 'edit') act.start(c); else if (a === 'delete') void act.remove(c); }}
        onClose={() => setSheet(false)} />}
    </div>
  );
}
